'use client';

import { useEffect } from 'react';
import { BuildBottomBar } from '~/components/Playground/BottomBar/BottomBar';
import type { MapBounds } from '~/components/Playground/mapConstants';
import PlaygroundMap from '~/components/Playground/PlaygroundMap';
import { PrimaryEditBar } from '~/components/Playground/PrimaryEditBar';
import type { Countries } from '~/libs/dato/queries/countries';
import type { Places } from '~/libs/dato/queries/places';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundStore } from '~/stores/playgroundStore';
import type { DpOrigin } from '~/types/Origin.types';
import type { PlaygroundContext } from '~/types/Playground.types';
import styles from './build.module.css';

const _OVERRIDE_PARAM_KEYS = ['nights', 'startDate', 'endDate', 'origin'] as const;

interface Props {
  initialPlaygroundContext: PlaygroundContext;
  allCmsPlaces: Places['allPlaces'];
  allCmsPlacesEnglish: Places['allPlaces'];
  allDpOrigins: DpOrigin[];
  allCmsCountries: Countries['allCountries'];
  datesType: 'nights' | 'calendar';
  tripType?: 'one-way-trip' | 'round-trip';
  nights: number | null;
  countryExtents: MapBounds | null;
  journeyId?: string;
}

export default function PlaygroundBuildClient({
  initialPlaygroundContext,
  allCmsPlaces,
  allCmsPlacesEnglish,
  allDpOrigins,
  allCmsCountries,
  datesType,
  tripType,
  nights,
  countryExtents,
  journeyId
}: Props) {
  const {
    setContext,
    setAllCmsPlaces,
    setAllCmsPlacesEnglish,
    setAllCmsCountries,
    setAllDpOrigins,
    setIsLoading,
    setCountryExtents,
    setNights,
    setCreateTripTab,
    setDatesTab,
    showDialog
  } = usePlaygroundActions();
  const playgroundContext = usePlaygroundContext();

  useEffect(() => {
    const initPlaygroundBuild = async () => {
      setIsLoading(false);
      await usePlaygroundStore.persist.rehydrate();
      const hydratedContext = usePlaygroundStore.getState().context;
      const isPersistedStateDefault = hydratedContext?.isDefaultContext ?? false;

      // CMS / DP data is not persisted (it is locale-specific), so always seed it from the server props.
      setAllCmsPlaces(allCmsPlaces);
      setAllCmsPlacesEnglish(allCmsPlacesEnglish);
      setAllCmsCountries(allCmsCountries);
      setAllDpOrigins(allDpOrigins);

      // The URL's origin always wins over persisted state. Otherwise a stale London context from an
      // earlier visit would mask e.g. `?origin=22370654`. Same origin keeps persisted edits across refresh.
      const isPersistedOriginStale = hydratedContext?.chosenOrigin?.id !== initialPlaygroundContext.chosenOrigin.id;

      if (isPersistedStateDefault || isPersistedOriginStale) {
        setContext(initialPlaygroundContext);
        setNights(nights);
        setCreateTripTab(tripType ?? 'round-trip');
        setDatesTab(datesType);
      }

      if (countryExtents) {
        setCountryExtents(countryExtents);
      }

      if (journeyId) {
        showDialog('edit-journey');
      }
    };
    initPlaygroundBuild();
  }, []);

  if (!playgroundContext) return <p>Loading…</p>;

  return (
    <div className={styles.container}>
      {process.env.NEXT_PUBLIC_AI_TRAVEL_PLANNER === 'true' ? (
        <div className={styles.floatingEditBar}>
          <PrimaryEditBar />
        </div>
      ) : (
        <PrimaryEditBar />
      )}

      <div className={styles.main}>
        <div className={styles.mapAndBottomBar}>
          <PlaygroundMap />
          <BuildBottomBar />
        </div>
      </div>
    </div>
  );
}
